/**
 * "People Watching" widget configuration.
 *
 * Two independent product groups with separate display and dismiss logic:
 *
 * ## Oestra (group: "oestra")
 * Pages: Peri LP, HRT LP, Oestra PQP.
 * - LP → shown once per session. Dismiss hides it on LP pages only.
 * - PQP → shown once per session even if LP was dismissed.
 *         Values = base + PQP_INCREMENT (applied once, persisted).
 *         Dismiss on PQP → widget is gone for the rest of the session.
 *
 * ## Face Cream (group: "face_cream")
 * Pages: Anti-Aging Face Cream PQP.
 * - Fully independent from Oestra (own random values, own dismiss state).
 * - No increment logic.
 * - Dismiss → widget is gone for the rest of the session.
 */

import { APP_ROUTES } from "~/constants/appRoutes";

/** Landing page (WordPress/Elementor) or Product Quick Page (Next.js). */
export type PageType = "lp" | "pqp";

/** Independent product groups with separate session state and dismiss logic. */
export type ProductGroup = "oestra" | "face_cream";

export interface PageConfig {
    /** Display name shown in the widget (e.g. "Oestra®"). */
    productName: string;
    /** Cloudinary thumbnail URL for the product image. */
    productImage: string;
    /** Page type — affects increment logic for the "oestra" group. */
    pageType: PageType;
    /** Product group — determines session keys and dismiss behavior. */
    group: ProductGroup;
}

const OESTRA_IMAGE =
    "https://res.cloudinary.com/dirscuuhp/image/upload/w_160/v1763473046/new_design/product-oestra_knz7pj.webp";

const FACE_CREAM_IMAGE =
    "https://res.cloudinary.com/dirscuuhp/image/upload/v1783363247/new_design/BodyMatched_Anti-Aging_Face_Cream_vji9mg.webp";

/**
 * Maps pathname → widget configuration.
 * Only pages listed here will display the widget.
 */
export const PAGE_CONFIG_MAP: Record<string, PageConfig> = {
    [APP_ROUTES.PQP_HORMONE_IMBALANCE]: {
        productName: "Oestra\u00ae",
        productImage: OESTRA_IMAGE,
        pageType: "pqp",
        group: "oestra",
    },
    [APP_ROUTES.PQP_ANTI_AGING_FACE_CREAM]: {
        productName: "Bodymatched\u00ae Anti-Aging\nFace Cream",
        productImage: FACE_CREAM_IMAGE,
        pageType: "pqp",
        group: "face_cream",
    },
};

/** Random range for the "X people are viewing" counter (per group). */
export const VIEWING_RANGE = { min: 25, max: 50 } as const;

/** Random range for the "X women bought it today" counter (per group). */
export const PURCHASED_RANGE = { min: 300, max: 500 } as const;

/**
 * One-time increment applied when Oestra widget is first shown on a PQP page.
 * Values are added to the base and persisted — they never decrease on navigation back.
 */
export const PQP_INCREMENT = { viewing: 8, purchased: 5 } as const;

/**
 * SessionStorage keys, namespaced per product group.
 *
 * Oestra has separate dismiss flags for LP and PQP:
 * - DISMISSED_LP: closing on LP still allows PQP to show.
 * - DISMISSED_PQP: closing on PQP hides widget for the rest of the session.
 * - PQP_INCREMENTED: ensures +viewing/+purchased is applied only once.
 *
 * Face Cream has a single dismiss flag (no LP/PQP distinction).
 */
export const SESSION_KEYS = {
    oestra: {
        VIEWING: "pw_oestra_viewing",
        PURCHASED: "pw_oestra_purchased",
        DISMISSED_LP: "pw_oestra_dismissed_lp",
        DISMISSED_PQP: "pw_oestra_dismissed_pqp",
        PQP_INCREMENTED: "pw_oestra_pqp_incremented",
    },
    face_cream: {
        VIEWING: "pw_face_cream_viewing",
        PURCHASED: "pw_face_cream_purchased",
        DISMISSED: "pw_face_cream_dismissed",
    },
} as const;
